import { useState, useRef } from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCalendar,
    faClock,
    faUser,
    faPlus,
    faTag,
    faEye,
} from "@fortawesome/free-solid-svg-icons";

import RegisterSessionPopup from "../services/session/popupRegisterSession/RegisterSessionPopup";
import EditSessionPopup from "../services/session/popupEditSession/EditSessionPopup";
import PaginatorComponent from "../general/auxiliar/ui/PaginatorComponent";
import EmptyTable from "../general/auxiliar/table/EmptyTable";
import LoadingTable from "../general/auxiliar/table/LoadingTable";
import OverviewTable from "../general/auxiliar/table/OverviewTable";
import OverviewCardList from "../general/auxiliar/table/OverviewCard";
import usePaginationFilterEffects from "../../auxiliar/customHooks/usePaginationFilterEffects";
import { handleSubmit, getTableState } from "../../auxiliar/auxFunctions";
import { getAllSessions } from "../../actions/session";

const SessionCardList = ({ sessions, onEditSessionClick }) => {
    const renderTitle = (session) => `${session.start_datetime.substring(0, 10)}`;

    const renderSubtitle = (session) => (
        <div className="flex items-center gap-2 mt-1">
            <div className="flex items-center gap-1 text-base-content/70">
                <FontAwesomeIcon
                    icon={faClock}
                    className="w-3 h-3 text-base-content/40 flex-shrink-0"
                />
                <span className="text-xs sm:text-sm">
                    {session.start_datetime.substring(11, 16)} -{" "}
                    {session.finish_datetime.substring(11, 16)}
                </span>
            </div>
        </div>
    );

    const renderFooter = (session) => (
        <div className="space-y-2 text-xs sm:text-sm">
            <div className="flex items-center gap-2 text-base-content/70">
                <FontAwesomeIcon
                    icon={faUser}
                    className="w-3 h-3 text-base-content/40 flex-shrink-0"
                />
                <span className="truncate">{session.physio_name}</span>
            </div>
            <div className="flex items-center gap-2 text-base-content/70">
                <FontAwesomeIcon
                    icon={faTag}
                    className="w-3 h-3 text-base-content/40 flex-shrink-0"
                />
                <span className="truncate">{session.type}</span>
            </div>
        </div>
    );

    const renderActions = (session) => (
        <button
            type="button"
            className="btn btn-primary btn-xs sm:btn-sm text-xs sm:text-sm"
            onClick={handleSubmit(onEditSessionClick, session)}
        >
            <FontAwesomeIcon icon={faEye} className="mr-1 sm:mr-2 text-xs sm:text-sm" />
            <span className="sm:hidden">Ver</span>
            <span className="hidden sm:inline">Detalles</span>
        </button>
    );

    return (
        <OverviewCardList
            data={sessions}
            renderTitle={renderTitle}
            renderSubtitle={renderSubtitle}
            renderFooter={renderFooter}
            renderActions={renderActions}
        />
    );
};

const SessionTable = ({ sessions, onEditSessionClick }) => {
    const columns = [
        {
            label: "Tipo",
            icon: faTag,
            width: "w-[29%]",
        },
        {
            label: "Fisioterapeuta",
            icon: faUser,
            width: "w-[29%]",
        },
        {
            label: "Fecha",
            icon: faCalendar,
            width: "w-[12%]",
        },
        {
            label: "Horario",
            icon: faClock,
            width: "w-[12%]",
        },
    ];

    const getRowData = (session, sessionId) => [
        <div
            key={`${sessionId}-type`}
            className="font-medium text-base-content truncate"
            title={session.type}
        >
            {session.type}
        </div>,
        <div
            key={`${sessionId}-physio`}
            className="text-base-content/70 truncate"
            title={session.physio_name}
        >
            {session.physio_name}
        </div>,
        <div key={`${sessionId}-date`} className="text-base-content/70">
            {session.start_datetime.substring(0, 10)}
        </div>,
        <div key={`${sessionId}-time`} className="text-base-content/70">
            {session.start_datetime.substring(11, 16)} - {session.finish_datetime.substring(11, 16)}
        </div>,
    ];

    const handleSessionNavigation = (sessionId) => {
        const session = sessions.content[sessionId];
        onEditSessionClick(session);
    };

    const getDetailPath = (sessionId) => sessionId;

    return (
        <OverviewTable
            data={sessions}
            navigate={handleSessionNavigation}
            columns={columns}
            getRowData={getRowData}
            getDetailPath={getDetailPath}
            actionsWidth="w-[12%]"
        />
    );
};

const OverviewSessionsInPatient = () => {
    const openTooltipRegister = useRef();
    const openTooltipEdit = useRef();

    const [refreshBool, setRefreshBool] = useState(false);
    const refresh = () => setRefreshBool(!refreshBool);

    const { patient_id: patientId } = useParams();
    const { id: activeClinicId } = useSelector((state) => state.activeClinic);
    const [sessions, setSessions] = useState({ waiting: true });
    const [filter, setFilter] = useState({
        per_page: 10,
        page: 1,
        clinic_id: activeClinicId,
        patient_id: patientId,
        order: "date",
    });

    const disabled = usePaginationFilterEffects(
        filter,
        undefined,
        getAllSessions,
        setSessions,
        setFilter,
        refreshBool,
    );

    const onEditSessionClick = (session) => {
        openTooltipEdit.current({
            ...session,
            id: session.session_id,
            event_type: "individual",
            startAt: session.start_datetime,
            endAt: session.finish_datetime,
        });
    };

    const onNewSessionClick = () => {
        const date = new Date();
        openTooltipRegister.current(
            {
                patient_id: patientId,
                startAt: format(date, "yyyy-MM-dd'T'HH:mm").toString(),
                endAt: format(
                    date.setTime(date.getTime() + 60 * 60 * 1000),
                    "yyyy-MM-dd'T'HH:mm",
                ).toString(),
            },
            true,
        );
    };

    const tableState = getTableState(sessions);

    const renderContent = () => {
        switch (tableState) {
            case "loading":
                return <LoadingTable message="Cargando sesiones..." />;

            case "empty":
                return (
                    <EmptyTable
                        icon={faCalendar}
                        title="No hay sesiones registradas"
                        description="Añade la primera sesión para comenzar a gestionar las citas del paciente."
                        buttonText="Nueva Sesión"
                        onButtonClick={handleSubmit(onNewSessionClick)}
                    />
                );

            case "hasData":
            default:
                return (
                    <>
                        <SessionCardList
                            sessions={sessions}
                            onEditSessionClick={onEditSessionClick}
                        />
                        <SessionTable sessions={sessions} onEditSessionClick={onEditSessionClick} />
                    </>
                );
        }
    };

    return (
        <section>
            <h2 className="sr-only">
                Detalles de las sesiones reservadas de tu paciente en MiFisio Agenda
            </h2>

            <h3 className="text-primary text-2xl mb-4">Sesiones Reservadas</h3>

            <div className="my-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
                {/* Action button */}
                <button
                    className="btn btn-primary btn-sm w-full sm:w-auto"
                    onClick={handleSubmit(onNewSessionClick)}
                >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Nueva Sesión
                </button>

                {/* Pagination - only show container when needed */}
                <div className="w-full sm:w-auto">
                    <PaginatorComponent
                        pagination={sessions.pagination}
                        setFilter={setFilter}
                        filter={filter}
                        disabled={disabled}
                    />
                </div>
            </div>

            {renderContent()}

            {/* Pagination for small screens - duplicated to improve UX */}
            <div className="w-full my-5 sm:w-auto lg:hidden">
                <PaginatorComponent
                    pagination={sessions.pagination}
                    setFilter={setFilter}
                    filter={filter}
                    disabled={disabled}
                    showTotalItems={false}
                />
            </div>

            <RegisterSessionPopup childFunc={openTooltipRegister} refresh={refresh} />
            <EditSessionPopup childFunc={openTooltipEdit} refresh={refresh} />
        </section>
    );
};

export default OverviewSessionsInPatient;
